(() => {
  const fieldNames = ['boutique_name', 'street', 'town', 'country', 'website', 'email'];

  function initialiseRetailer(root) {
    if (!root || root.dataset.retailerInitialised === 'true') return;
    root.dataset.retailerInitialised = 'true';

    root.querySelectorAll('[data-retailer-toggle]').forEach((button) => {
      const list = document.getElementById(button.getAttribute('aria-controls'));
      if (!list) return;
      list.classList.add('is-collapsed');
      button.setAttribute('aria-expanded', 'false');
      button.children[0].textContent = '+';
      button.children[1].textContent = button.dataset.more;
      button.hidden = false;
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        list.classList.toggle('is-collapsed', !expanded);
        button.children[0].textContent = expanded ? '−' : '+';
        button.children[1].textContent = expanded ? button.dataset.less : button.dataset.more;
      });
    });

    const form = root.querySelector('[data-retailer-form]');
    if (!form) return;
    let messages;
    try {
      messages = JSON.parse(form.querySelector('[data-retailer-messages]').textContent);
    } catch (_) {
      return;
    }
    const submit = form.querySelector('[type="submit"]');
    const result = form.querySelector('[data-retailer-result]');
    const error = form.querySelector('[data-retailer-error]');
    const inputs = fieldNames.map((name) => form.elements.namedItem(name));
    const storageKey = `dama-retailer-request:${form.id}`;
    let endpoint;
    let activeRequest = null;
    let pending = false;

    function showError(key) {
      result.hidden = true;
      error.textContent = messages[key] || messages.error_generic;
      error.hidden = false;
    }

    try {
      if (!form.dataset.endpoint.trim()) throw new Error('missing_endpoint');
      endpoint = new URL(form.dataset.endpoint, window.location.origin);
      if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password) throw new Error('invalid_endpoint');
      endpoint.hash = '';
    } catch (_) {
      form.addEventListener('submit', (event) => event.preventDefault());
      showError('error_unavailable');
      return;
    }

    submit.disabled = false;
    form.addEventListener('input', () => {
      if (pending) return;
      error.hidden = true;
      result.hidden = true;
      submit.disabled = false;
      submit.textContent = messages.submit;
      delete form.dataset.submissionState;
    });

    async function requestIdFor(payload) {
      const serialised = JSON.stringify(payload);
      let fingerprint = serialised;
      let canPersist = false;
      if (window.crypto?.subtle) {
        const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(serialised));
        fingerprint = Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
        canPersist = true;
      }
      if (activeRequest?.fingerprint === fingerprint) return activeRequest.requestId;
      if (canPersist) {
        try {
          const stored = JSON.parse(window.sessionStorage.getItem(storageKey));
          if (stored?.fingerprint === fingerprint && /^[0-9a-f-]{36}$/i.test(stored.requestId)) {
            activeRequest = stored;
            return stored.requestId;
          }
        } catch (_) {}
      }
      const requestId = window.crypto.randomUUID();
      activeRequest = { fingerprint, requestId };
      if (canPersist) {
        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify(activeRequest));
        } catch (_) {}
      }
      return requestId;
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending || !form.reportValidity()) return;
      const payload = Object.fromEntries(inputs.map((input) => [input.name, input.value.trim()]));
      if (fieldNames.some((name) => name !== 'website' && !payload[name])) {
        showError('error_validation');
        return;
      }
      payload.locale = form.dataset.locale || document.documentElement.lang || 'en';
      payload.source_url = window.location.origin + window.location.pathname;
      pending = true;
      submit.disabled = true;
      submit.textContent = messages.sending;
      form.setAttribute('aria-busy', 'true');
      inputs.forEach((input) => { input.disabled = true; });
      error.hidden = true;
      result.hidden = true;
      let succeeded = false;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 25000);

      try {
        payload.request_id = await requestIdFor(payload);
        const response = await fetch(endpoint.href, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
          redirect: 'error'
        });
        if (response.status === 429) {
          showError('error_rate_limit');
          return;
        }
        if (response.status >= 500) {
          showError('error_unavailable');
          return;
        }
        let data;
        try {
          data = await response.json();
        } catch (_) {
          showError('error_network');
          return;
        }
        if (!response.ok || data?.ok !== true) {
          const validationCodes = ['invalid_input', 'validation_error', 'invalid_address', 'missing_fields', 'invalid_email'];
          if (response.status === 400 || response.status === 422 || validationCodes.includes(data?.code)) {
            showError('error_validation');
          } else if (data?.code === 'rate_limited') {
            showError('error_rate_limit');
          } else {
            showError('error_generic');
          }
          return;
        }
        if (!['open', 'held', 'check'].includes(data.status) || data.enquiry_received !== true) {
          showError('error_network');
          return;
        }
        result.querySelector('[data-retailer-result-title]').textContent = messages[`${data.status}_label`];
        result.querySelector('[data-retailer-result-body]').textContent = messages[`${data.status}_body`];
        result.querySelector('[data-retailer-result-received]').textContent = messages.received;
        result.dataset.status = data.status;
        result.hidden = false;
        result.focus({ preventScroll: true });
        result.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        succeeded = true;
      } catch (_) {
        showError('error_network');
      } finally {
        window.clearTimeout(timeout);
        pending = false;
        inputs.forEach((input) => { input.disabled = false; });
        submit.textContent = succeeded ? (messages.sent || messages.received) : messages.submit;
        submit.disabled = succeeded;
        form.dataset.submissionState = succeeded ? 'success' : 'idle';
        form.removeAttribute('aria-busy');
      }
    });
  }

  function initialiseWithin(scope) {
    if (scope.matches?.('[data-dama-retailer]')) initialiseRetailer(scope);
    scope.querySelectorAll?.('[data-dama-retailer]').forEach(initialiseRetailer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseWithin(document), { once: true });
  } else {
    initialiseWithin(document);
  }
  document.addEventListener('shopify:section:load', (event) => initialiseWithin(event.target));
})();
